import { apiClient } from '@/api/core/apiClient.ts';
import { API_CONFIG } from '@/api/core/apiConfig.ts';
import { buildQueryString } from '@/api/shared/queryBuilder.ts';
import type { ApiResponse } from '@/api/shared/types.ts';
import type {
  LC_EmployeeDocumentPagedResult,
  LC_GetCompanyFoldersListOut,
  LC_GetDocumentTypesListOut,
  EmployeeDocumentPage,
  CompanyFolder,
  DocumentType,
  EmployeeDocumentListParams,
} from '../types/employeeDocument.types';
import {
  mapEmployeeDocumentItem,
  mapCompanyFolderItem,
  mapDocumentTypeItem,
} from '../mappers/employeeDocument.mapper';


export class EmployeeDocumentService {
  async list(params: EmployeeDocumentListParams = {}): Promise<ApiResponse<EmployeeDocumentPage>> {
    const query = buildQueryString({
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      folderId: params.folderId,
      documentTypeId: params.documentTypeId,
      page: params.page,
      pageSize: params.pageSize,
      searchText: params.searchText,
      orderBy: params.orderBy,
      orderDirection: params.orderDirection,
    });

    const response = await apiClient.get<LC_EmployeeDocumentPagedResult>(
      `${API_CONFIG.ENDPOINTS.EMPLOYEE_DOCUMENT.LIST}${query}`,
    );

    if (!response.success || !response.data) return { ...response, data: null };

    const page: EmployeeDocumentPage = {
      data: (response.data.data ?? []).map(mapEmployeeDocumentItem),
      page: response.data.pagination.currentPage,
      pageSize: response.data.pagination.pageSize,
      totalRecords: response.data.pagination.totalRecords,
      totalPages: response.data.pagination.totalPages,
      hasNextPage: response.data.pagination.hasNextPage,
      hasPreviousPage: response.data.pagination.hasPreviousPage,
    };

    return { ...response, data: page };
  }

  async download(documentId: number): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    const url = API_CONFIG.ENDPOINTS.EMPLOYEE_DOCUMENT.DOWNLOAD(documentId);
    return apiClient.downloadBlobWithFilename(url, { method: 'GET' });
  }

  async getCompanyFolders(): Promise<ApiResponse<CompanyFolder[]>> {
    const response = await apiClient.get<LC_GetCompanyFoldersListOut>(
      API_CONFIG.ENDPOINTS.CATALOG.COMPANY_FOLDERS,
    );

    if (!response.success || !response.data) return { ...response, data: null };

    const folders = response.data.map(mapCompanyFolderItem);
    return { ...response, data: folders };
  }

  async getDocumentTypes(signal?: AbortSignal): Promise<ApiResponse<DocumentType[]>> {
    const response = await apiClient.get<LC_GetDocumentTypesListOut>(
      API_CONFIG.ENDPOINTS.CATALOG.DOCUMENT_TYPES,
      { signal },
    );

    if (!response.success || !response.data) return { ...response, data: null };

    const types = response.data.map(mapDocumentTypeItem);
    return { ...response, data: types };
  }
}

export const employeeDocumentService = new EmployeeDocumentService();
